import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Megaphone, Plus, Target, Calendar, Wallet, Users, Trash2, Pencil, ChevronDown,
  Eye, EyeOff, Building2, X, Gauge, Inbox, Check,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SimulationBadge, isSimulation } from "@/components/provenance/ProvenanceBadges";
import { useAuth } from "@/auth/AuthContext";
import { useDemo, DemoHint } from "@/components/demo/DemoUi";
import DemoCampaignScenario from "@/components/demo/DemoCampaignScenario";
import { toast } from "sonner";
import { isMauritiusDestination, MAURITIUS_DEMO_CAMPAIGN } from "@/lib/mauritiusDemoJourney";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Draft", "Planned", "Active", "Completed"];
const statusStyle = {
  Draft: "bg-slate-100 text-slate-600",
  Planned: "bg-[#E0F2FE] text-[#0369A1]",
  Active: "bg-[#DCFCE7] text-[#047857]",
  Completed: "bg-[#F3EFE3] text-[#7A6A3C]",
};
const VIS_FIELDS = [
  { key: "objective", label: "Objective" },
  { key: "market", label: "Market" },
  { key: "period", label: "Period" },
  { key: "target_audience", label: "Target Audience" },
  { key: "budget", label: "Budget" },
  { key: "kpis", label: "KPIs" },
];
const isAdminRole = (role) => ["Owner", "Administrator"].includes(role);
const fmtMoney = (b) => b ? `${new Intl.NumberFormat("fr-FR").format(b.amount || 0)} ${b.currency || ""}` : "—";

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button type="button" data-testid={testid} disabled={disabled} role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-[#3E7256]" : "bg-slate-300"}`}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
  </button>
);

const emptyForm = () => ({
  objective: "", market: "", period: "", target_audience: "",
  budget: { amount: 0, currency: "EUR" }, kpis: [], status: "Draft", partner_org_ids: [],
  decision_context: null,
});

// Normalise incoming prefill KPIs (from an Opportunity): a value of 0/absent is treated as "no value
// known" (empty), never a real measured zero. Real values (incl. a genuine 0 from an edited campaign)
// are handled separately in the `editing` branch below.
const normalizePrefillKpis = (kpis = []) =>
  kpis.map((k) => ({ name: k.name || "", target: k.target ? k.target : "", current: k.current ? k.current : "" }));

function CampaignForm({ open, onClose, onSaved, activePartners, editing, orgId, prefill, readOnly = false }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        objective: editing.objective || "", market: editing.market || "", period: editing.period || "",
        target_audience: editing.target_audience || "",
        budget: editing.budget || { amount: 0, currency: "EUR" },
        kpis: editing.kpis || [], status: editing.status || "Draft",
        partner_org_ids: (editing.partners || []).map((p) => p.org_id),
        decision_context: editing.decision_context || null,
      });
    } else if (prefill) {
      setForm({ ...emptyForm(), objective: prefill.objective || "", market: prefill.market || "",
        period: prefill.period || "", target_audience: prefill.target_audience || "",
        budget: prefill.budget || { amount: 0, currency: "EUR" },
        kpis: normalizePrefillKpis(prefill.kpis),
        decision_context: prefill.decision_context || null });
    } else setForm(emptyForm());
  }, [editing, open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setKpi = (i, k, v) => setForm((f) => ({ ...f, kpis: f.kpis.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addKpi = () => setForm((f) => ({ ...f, kpis: [...f.kpis, { name: "", target: "", current: "" }] }));
  const rmKpi = (i) => setForm((f) => ({ ...f, kpis: f.kpis.filter((_, j) => j !== i) }));
  const togglePartner = (id) => setForm((f) => ({
    ...f, partner_org_ids: f.partner_org_ids.includes(id) ? f.partner_org_ids.filter((x) => x !== id) : [...f.partner_org_ids, id],
  }));

  const submit = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const payload = {
        objective: form.objective, market: form.market, period: form.period,
        target_audience: form.target_audience,
        budget: { amount: parseFloat(form.budget.amount) || 0, currency: form.budget.currency || "EUR" },
        kpis: form.kpis.map((k) => ({ name: k.name, target: parseFloat(k.target) || 0, current: parseFloat(k.current) || 0 })),
        status: form.status,
        decision_context: form.decision_context,
      };
      if (editing) {
        await api.patch(`/organizations/${orgId}/campaigns/${editing.id}`, payload);
      } else {
        await api.post(`/organizations/${orgId}/campaigns`, { ...payload, partner_org_ids: form.partner_org_ids });
      }
      toast.success(editing ? "Campagne mise à jour" : "Campagne créée");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="campaign-form">
        <DialogHeader><DialogTitle>{editing ? "Modifier la campagne" : "Créer une campagne"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {readOnly && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800" data-testid="campaign-demo-readonly">
              Aperçu de démonstration : vous pouvez explorer le brief prérempli, mais aucune campagne réelle ne sera créée.
            </div>
          )}
          {form.decision_context && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="campaign-decision-context">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Contexte Lodge</span>
                {form.decision_context.provenance && (
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-[#3E7256]" data-testid="campaign-context-source">
                    {form.decision_context.provenance}
                  </span>
                )}
                {form.decision_context.is_demo && <SimulationBadge />}
              </div>
              {form.decision_context.signal && (
                <p className="mt-2 text-sm font-semibold text-[#1E3D2B]" data-testid="campaign-context-signal">{form.decision_context.signal}</p>
              )}
              {form.decision_context.evidence && (
                <p className="mt-1 text-xs leading-relaxed text-[#475569]" data-testid="campaign-context-evidence">{form.decision_context.evidence}</p>
              )}
              {form.decision_context.data_used?.length > 0 && (
                <p className="mt-2 text-[11px] text-slate-500" data-testid="campaign-context-data-used">
                  <span className="font-semibold">Données utilisées :</span> {form.decision_context.data_used.join(", ")}
                </p>
              )}
              <p className="mt-2 text-[10px] text-slate-400">Préremplissage à valider avant création. Aucun média n’est activé automatiquement.</p>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Objective</label>
            <Input data-testid="campaign-objective" value={form.objective} onChange={(e) => set("objective", e.target.value)} placeholder="ex. Notoriété hiver sur le marché allemand" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Market</label>
              <Input data-testid="campaign-market" value={form.market} onChange={(e) => set("market", e.target.value)} placeholder="ex. Germany" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Period</label>
              <Input data-testid="campaign-period" value={form.period} onChange={(e) => set("period", e.target.value)} placeholder="ex. Q4 2025" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Target Audience</label>
            <Textarea data-testid="campaign-audience" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} placeholder="ex. Voyageurs CSP+ 35-55, urbains" rows={2} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Budget</label>
              <Input data-testid="campaign-budget" type="number" value={form.budget.amount} onChange={(e) => set("budget", { ...form.budget, amount: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Devise</label>
              <Input data-testid="campaign-currency" value={form.budget.currency} onChange={(e) => set("budget", { ...form.budget, currency: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Statut</label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="campaign-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* KPIs */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">KPIs</label>
              <button type="button" data-testid="add-kpi" onClick={addKpi} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:underline"><Plus size={12} /> Ajouter un KPI</button>
            </div>
            <div className="space-y-2">
              {form.kpis.map((k, i) => (
                <div key={i} className="flex flex-col gap-2 rounded-lg border border-slate-100 p-2.5 sm:flex-row sm:items-end sm:gap-2 sm:border-0 sm:p-0" data-testid={`kpi-row-${i}`}>
                  <div className="flex-1">
                    <label htmlFor={`kpi-name-${i}`} className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Nom du KPI</label>
                    <Input id={`kpi-name-${i}`} data-testid={`kpi-name-${i}`} placeholder="Nom (ex. Réservations)" value={k.name} onChange={(e) => setKpi(i, "name", e.target.value)} />
                  </div>
                  <div className="w-full sm:w-28">
                    <label htmlFor={`kpi-target-${i}`} className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Objectif</label>
                    <Input id={`kpi-target-${i}`} data-testid={`kpi-target-${i}`} type="number" placeholder="Objectif" value={k.target} onChange={(e) => setKpi(i, "target", e.target.value)} />
                  </div>
                  <div className="w-full sm:w-28">
                    <label htmlFor={`kpi-current-${i}`} className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Actuel</label>
                    <Input id={`kpi-current-${i}`} data-testid={`kpi-current-${i}`} type="number" placeholder="Actuel" value={k.current} onChange={(e) => setKpi(i, "current", e.target.value)} />
                  </div>
                  <button type="button" aria-label={`Supprimer le KPI ${i + 1}`} data-testid={`rm-kpi-${i}`} onClick={() => rmKpi(i)} className="self-end pb-1 text-slate-400 hover:text-rose-600 sm:pb-2.5"><X size={16} /></button>
                </div>
              ))}
              {form.kpis.length === 0 && <p className="text-xs text-slate-400">Aucun KPI. Valeurs saisies manuellement (pas de connexion publicitaire).</p>}
            </div>
          </div>

          {/* Partners (only on create) */}
          {!editing && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Partenaires (partenaires actifs uniquement)</label>
              {activePartners.length === 0 ? (
                <p className="text-xs text-slate-400">Aucun partenaire actif. Ajoutez-en dans le module Partners.</p>
              ) : (
                <div className="space-y-1.5">
                  {activePartners.map((p) => (
                    <label key={p.org_id} data-testid={`select-partner-${p.org_id}`} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                      <input type="checkbox" checked={form.partner_org_ids.includes(p.org_id)} onChange={() => togglePartner(p.org_id)} className="h-4 w-4 accent-[#3E7256]" />
                      <Building2 size={14} className="text-[#3E7256]" /> {p.org?.name}
                      <span className="text-xs text-slate-400">{p.org?.type}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="campaign-save" onClick={submit} disabled={saving || readOnly} className="bg-[#1E3D2B] hover:bg-[#2C5540]">
            {readOnly ? "Création indisponible en démo" : (editing ? "Enregistrer" : "Créer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TIMELINE_STAGES = ["Planned", "Active", "Completed"];
const stageLabel = { Planned: "Planifiée", Active: "Active", Completed: "Terminée" };

function CampaignTimeline({ status }) {
  const idx = TIMELINE_STAGES.indexOf(status); // Draft => -1 (not started)
  return (
    <div className="flex items-center" data-testid={`timeline-${status}`}>
      {TIMELINE_STAGES.map((stage, i) => {
        const done = i < idx;
        const current = i === idx;
        const dotCls = done
          ? "bg-[#3E7256] text-white"
          : current
          ? "bg-[#3E7256] text-white ring-4 ring-[#DDEBE2]"
          : "bg-slate-200 text-slate-400";
        return (
          <React.Fragment key={stage}>
            <div className="flex flex-col items-center gap-1">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition-colors ${dotCls}`}>
                {done ? <Check size={12} /> : i + 1}
              </div>
              <span className={`text-[10px] font-semibold ${done || current ? "text-[#2C5540]" : "text-slate-400"}`}>{stageLabel[stage]}</span>
            </div>
            {i < TIMELINE_STAGES.length - 1 && (
              <div className={`mx-1 mt-[-14px] h-0.5 flex-1 rounded-full ${i < idx ? "bg-[#3E7256]" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function KpiBars({ kpis }) {
  if (!kpis || kpis.length === 0) return <p className="text-xs text-slate-400">Aucun KPI</p>;
  return (
    <div className="space-y-1.5">
      {kpis.map((k, i) => {
        const pct = k.target > 0 ? Math.min(100, Math.round((k.current / k.target) * 100)) : 0;
        return (
          <div key={i}>
            <div className="flex items-center justify-between text-xs"><span className="text-slate-500">{k.name || "—"}</span><span className="font-mono text-slate-600">{k.current}/{k.target}</span></div>
            <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#3E7256]" style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function MauritiusCampaignExample({ onUse, canUse }) {
  const campaign = MAURITIUS_DEMO_CAMPAIGN;
  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5" data-testid="mauritius-campaign-example">
      <div className="flex flex-wrap items-center gap-2">
        <SimulationBadge />
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-700">Exemple Maurice</span>
      </div>
      <h2 className="mt-3 font-heading text-lg font-semibold text-[#17201A]">{campaign.objective}</h2>
      <p className="mt-1 text-xs text-violet-700">Scénario illustratif non activé · aucune campagne externe créée.</p>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Marché</p><p>{campaign.market}</p></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Période</p><p>{campaign.period}</p></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Budget simulé</p><p>{fmtMoney(campaign.budget)}</p></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Canaux</p><p>{campaign.channels.join(" · ")}</p></div>
      </div>
      <div className="mt-4"><p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">KPIs simulés</p><KpiBars kpis={campaign.kpis} /></div>
      {canUse && (
        <button type="button" data-testid="use-mauritius-campaign-example" onClick={onUse}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]">
          <Plus size={15} /> Utiliser comme modèle
        </button>
      )}
    </div>
  );
}

export default function Campaigns() {
  const { activeOrgId, activeMembership } = useAuth();
  const { readOnly, copy: demoCopy } = useDemo();
  const admin = isAdminRole(activeMembership?.role);
  const [tab, setTab] = useState("mine");
  const [campaigns, setCampaigns] = useState([]);
  const [shared, setShared] = useState([]);
  const [activePartners, setActivePartners] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const showMauritiusExample = campaigns.length === 0 && !readOnly && isMauritiusDestination(activeMembership?.org || {});
  const useMauritiusExample = () => {
    setEditing(null);
    setPrefill({
      objective: MAURITIUS_DEMO_CAMPAIGN.objective,
      market: MAURITIUS_DEMO_CAMPAIGN.market,
      period: MAURITIUS_DEMO_CAMPAIGN.period,
      target_audience: MAURITIUS_DEMO_CAMPAIGN.target_audience,
      budget: MAURITIUS_DEMO_CAMPAIGN.budget,
      kpis: MAURITIUS_DEMO_CAMPAIGN.kpis,
    });
    setFormOpen(true);
  };

  // Open a pre-filled create form when arriving from an Opportunity.
  useEffect(() => {
    if (location.state?.prefill) {
      setEditing(null);
      setPrefill(location.state.prefill);
      setFormOpen(true);
      navigate("/campaigns", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const loadMine = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/campaigns`);
    setCampaigns(data.campaigns || []);
  }, [activeOrgId]);
  const loadShared = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/campaigns/shared`);
    setShared(data.campaigns || []);
  }, [activeOrgId]);
  const loadPartners = useCallback(async () => {
    if (!activeOrgId) return;
    const { data } = await api.get(`/organizations/${activeOrgId}/partners`);
    // Normalise partnership shape {partner:{id,name,...}} -> {org_id, org}
    setActivePartners((data.active || []).map((a) => ({ org_id: a.partner?.id, org: a.partner })));
  }, [activeOrgId]);

  useEffect(() => { loadMine(); loadShared(); loadPartners(); }, [loadMine, loadShared, loadPartners]);

  const removeCampaign = async (id) => {
    try { await api.delete(`/organizations/${activeOrgId}/campaigns/${id}`); toast.success("Campagne supprimée"); loadMine(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const quickStatus = async (id, status) => {
    try { await api.patch(`/organizations/${activeOrgId}/campaigns/${id}`, { status }); loadMine(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const setVisibility = async (cid, partnerId, vis) => {
    setCampaigns((cs) => cs.map((c) => c.id === cid ? { ...c, partners: c.partners.map((p) => p.org_id === partnerId ? { ...p, visibility: vis } : p) } : c));
    try { await api.patch(`/organizations/${activeOrgId}/campaigns/${cid}/partners/${partnerId}`, vis); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); loadMine(); }
  };
  const addPartner = async (cid, partnerId) => {
    try { await api.post(`/organizations/${activeOrgId}/campaigns/${cid}/partners`, { partner_org_id: partnerId }); loadMine(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };
  const removePartner = async (cid, partnerId) => {
    try { await api.delete(`/organizations/${activeOrgId}/campaigns/${cid}/partners/${partnerId}`); loadMine(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]"><Megaphone size={18} className="text-[#5C9873]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">Campaigns</h1>
            <p className="text-sm text-[#64748B]">Créez des activations, associez vos partenaires et contrôlez ce que chacun voit.</p>
          </div>
        </div>
        {tab === "mine" && admin && !readOnly && (
          <button data-testid="new-campaign-btn" onClick={() => { setEditing(null); setPrefill(null); setFormOpen(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540]">
            <Plus size={16} /> Créer une campagne
          </button>
        )}
        {tab === "mine" && admin && readOnly && (
          <div className="text-right">
            <button data-testid="new-campaign-btn-disabled" disabled title={demoCopy.readOnlyHint}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-400 cursor-not-allowed">
              <Plus size={16} /> Créer une campagne
            </button>
            <DemoHint />
          </div>
        )}
      </div>

      <DemoCampaignScenario />

      <div className="flex gap-6 border-b border-slate-200">
        <button data-testid="tab-mine" onClick={() => setTab("mine")} className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold ${tab === "mine" ? "border-[#1E3D2B] text-[#1E3D2B]" : "border-transparent text-slate-400 hover:text-slate-600"}`}><Megaphone size={16} /> Mes campagnes {campaigns.length > 0 && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold">{campaigns.length}</span>}</button>
        <button data-testid="tab-shared" onClick={() => setTab("shared")} className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold ${tab === "shared" ? "border-[#1E3D2B] text-[#1E3D2B]" : "border-transparent text-slate-400 hover:text-slate-600"}`}><Inbox size={16} /> Partagées avec moi {shared.length > 0 && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold">{shared.length}</span>}</button>
      </div>

      {/* MINE */}
      {tab === "mine" && (
        campaigns.length === 0 ? (
          <div className="space-y-4" data-testid="campaigns-empty">
            {showMauritiusExample && <MauritiusCampaignExample onUse={useMauritiusExample} canUse={admin && !readOnly} />}
            <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center">
              <Megaphone size={34} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">Aucune campagne réelle pour le moment</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">Créez votre première activation et associez vos partenaires.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {campaigns.map((c) => {
              const addable = activePartners.filter((ap) => !c.partners.some((p) => p.org_id === ap.org_id));
              return (
                <div key={c.id} data-testid={`campaign-card-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <span className={`mb-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusStyle[c.status]}`} data-testid={`campaign-status-${c.id}`}>{c.status}</span>
                      {isSimulation(c) && <SimulationBadge className="mb-2 ml-1.5 align-middle" />}
                      <h3 className="font-heading text-base font-semibold text-[#17201A]">{c.objective || "Sans objectif"}</h3>
                    </div>
                    {admin && (
                      <div className="flex items-center gap-1">
                        <button data-testid={`edit-campaign-${c.id}`} onClick={() => { setEditing(c); setPrefill(null); setFormOpen(true); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-[#3E7256]"><Pencil size={15} /></button>
                        <button data-testid={`delete-campaign-${c.id}`} onClick={() => removeCampaign(c.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-rose-600"><Trash2 size={15} /></button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 px-2"><CampaignTimeline status={c.status} /></div>

                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                    <div><p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Target size={11} /> Market</p><p className="text-[#17201A]">{c.market || "—"}</p></div>
                    <div><p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Calendar size={11} /> Period</p><p className="text-[#17201A]">{c.period || "—"}</p></div>
                    <div><p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Wallet size={11} /> Budget</p><p className="text-[#17201A]">{fmtMoney(c.budget)}</p></div>
                  </div>

                  {c.target_audience && <div className="mt-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Target Audience</p><p className="text-sm text-[#475569]">{c.target_audience}</p></div>}

                  {c.decision_context && (
                    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3" data-testid={`campaign-context-${c.id}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        Recommandation {c.decision_context.provenance || "Lodge"}
                      </p>
                      {c.decision_context.evidence && <p className="mt-1 text-xs leading-relaxed text-[#475569]">{c.decision_context.evidence}</p>}
                    </div>
                  )}

                  <div className="mt-4"><p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Gauge size={11} /> KPIs</p><KpiBars kpis={c.kpis} /></div>

                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                    <span className="flex items-center gap-1.5 text-xs text-slate-500"><Users size={13} /> {c.partners.length} partenaire{c.partners.length > 1 ? "s" : ""}</span>
                    <div className="flex items-center gap-2">
                      {admin && (
                        <Select value={c.status} onValueChange={(v) => quickStatus(c.id, v)}>
                          <SelectTrigger className="h-7 w-[120px] text-xs" data-testid={`quick-status-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <button data-testid={`manage-partners-${c.id}`} onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3E7256] hover:underline">
                        <ChevronDown size={13} className={expanded === c.id ? "rotate-180 transition-transform" : "transition-transform"} /> Partenaires & visibilité
                      </button>
                    </div>
                  </div>

                  {expanded === c.id && (
                    <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-[#FBFCFB] p-3" data-testid={`partners-panel-${c.id}`}>
                      {c.partners.length === 0 && <p className="text-xs text-slate-400">Aucun partenaire associé.</p>}
                      {c.partners.map((p) => (
                        <div key={p.org_id} data-testid={`campaign-partner-${c.id}-${p.org_id}`} className="rounded-lg border border-slate-200 bg-white p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-[#17201A]"><Building2 size={14} className="text-[#3E7256]" /> {p.org?.name}</span>
                            {admin && <button data-testid={`rm-partner-${c.id}-${p.org_id}`} onClick={() => removePartner(c.id, p.org_id)} className="text-slate-300 hover:text-rose-600"><X size={15} /></button>}
                          </div>
                          <p className="mb-2 text-[11px] text-slate-400">Ce partenaire ne voit que les sections activées :</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
                            {VIS_FIELDS.map((f) => (
                              <div key={f.key} className="flex items-center justify-between">
                                <span className="flex items-center gap-1 text-xs text-slate-600">{p.visibility?.[f.key] ? <Eye size={12} className="text-[#3E7256]" /> : <EyeOff size={12} className="text-slate-300" />} {f.label}</span>
                                <Toggle testid={`vis-${c.id}-${p.org_id}-${f.key}`} checked={!!p.visibility?.[f.key]} disabled={!admin} onChange={(v) => setVisibility(c.id, p.org_id, { ...p.visibility, [f.key]: v })} />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {admin && addable.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Select onValueChange={(v) => addPartner(c.id, v)}>
                            <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`add-partner-select-${c.id}`}><SelectValue placeholder="Associer un partenaire actif…" /></SelectTrigger>
                            <SelectContent>{addable.map((ap) => <SelectItem key={ap.org_id} value={ap.org_id} className="text-xs">{ap.org?.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* SHARED */}
      {tab === "shared" && (
        shared.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center" data-testid="shared-empty">
            <Inbox size={34} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-600">Aucune campagne partagée avec vous</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">Quand un partenaire vous associe à une activation, elle apparaît ici (limitée à ce que vous êtes autorisé à voir).</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {shared.map((c) => (
              <div key={c.id} data-testid={`shared-card-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-2 flex items-center justify-between">
                  <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusStyle[c.status]}`}>{c.status}</span>
                  <span className="flex items-center gap-1 text-xs text-slate-400"><Building2 size={12} /> {c.owner_org?.name}</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-[#17201A]">{c.objective ?? <span className="text-slate-300">Objectif non partagé</span>}</h3>
                <div className="mt-4 px-2"><CampaignTimeline status={c.status} /></div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                  <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Market</p><p>{c.market ?? <span className="text-slate-300">Masqué</span>}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Period</p><p>{c.period ?? <span className="text-slate-300">Masqué</span>}</p></div>
                  <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Budget</p><p>{c.budget ? fmtMoney(c.budget) : <span className="text-slate-300">Masqué</span>}</p></div>
                </div>
                <div className="mt-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Target Audience</p><p className="text-sm text-[#475569]">{c.target_audience ?? <span className="text-slate-300">Masqué</span>}</p></div>
                <div className="mt-3"><p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">KPIs</p>{c.kpis ? <KpiBars kpis={c.kpis} /> : <span className="text-xs text-slate-300">Masqués</span>}</div>
              </div>
            ))}
          </div>
        )
      )}

      <CampaignForm open={formOpen} onClose={() => { setFormOpen(false); setPrefill(null); }} onSaved={loadMine} activePartners={activePartners} editing={editing} orgId={activeOrgId} prefill={prefill} readOnly={readOnly} />
    </div>
  );
}
